/**
 * Omzetting tussen de waarde van het mailvak en de HTML van de editor
 * (sessie 70, blok 3).
 *
 * De waarde van het mailvak is een string. Een motorconcept, een voorstel
 * van de copiloot of een oud opgeslagen vak is platte tekst; wat de editor
 * teruggeeft is HTML. `naarHtml` zet platte tekst om met precies dezelfde
 * regels als `naar_html` in `app/services/mail_opmaak.py` (lege regel is
 * een alinea, enkele regel een `<br>`, `- ` aan het begin een lijst, een
 * los adres een link). De voorbeelden in `mailopmaak.voorbeelden.json`
 * worden door vitest en pytest allebei gelezen, zodat de twee gelijk
 * blijven.
 */

const HTML_TAG =
  /<(?:p|br|div|ul|ol|li|a|b|strong|i|em|u|span|table|blockquote|h[1-6])(?:\s[^>]*)?\/?>/i;
const ADRES = /https?:\/\/[^\s<>"]+/gi;
const LIJSTREGEL = /^\s*[-•]\s+/;

/** True als de tekst al opmaak in HTML draagt (gelijk aan `is_html`). */
export function isHtml(tekst: string | null | undefined): boolean {
  return HTML_TAG.test(tekst ?? "");
}

/** Als `html.escape(..., quote=False)` in Python. */
function escape(tekst: string): string {
  return tekst.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Als `html.escape(..., quote=True)` in Python. */
function escapeAttribuut(tekst: string): string {
  return escape(tekst).replace(/"/g, "&quot;").replace(/'/g, "&#x27;");
}

/** Python `rstrip(tekens)`. */
function rstrip(tekst: string, tekens: string): string {
  let eind = tekst.length;
  while (eind > 0 && tekens.includes(tekst[eind - 1])) eind -= 1;
  return tekst.slice(0, eind);
}

function metLinks(regel: string): string {
  const uit: string[] = [];
  let positie = 0;
  for (const treffer of regel.matchAll(ADRES)) {
    const start = treffer.index ?? 0;
    const adres = rstrip(treffer[0], ".,;:!?)");
    uit.push(escape(regel.slice(positie, start)));
    uit.push(`<a href="${escapeAttribuut(adres)}">${escape(adres)}</a>`);
    positie = start + adres.length;
  }
  uit.push(escape(regel.slice(positie)));
  return uit.join("");
}

function blokNaarHtml(regels: string[]): string {
  const delen: string[] = [];
  const gewoon: string[] = [];
  const lijst: string[] = [];
  const sluitGewoon = () => {
    if (gewoon.length) delen.push(`<p>${gewoon.join("<br>\n")}</p>`);
    gewoon.length = 0;
  };
  const sluitLijst = () => {
    if (lijst.length) delen.push(`<ul>${lijst.map((item) => `<li>${item}</li>`).join("")}</ul>`);
    lijst.length = 0;
  };
  for (const regel of regels) {
    if (LIJSTREGEL.test(regel)) {
      sluitGewoon();
      lijst.push(metLinks(regel.replace(LIJSTREGEL, "")));
    } else {
      sluitLijst();
      gewoon.push(metLinks(regel.replace(/\s+$/, "")));
    }
  }
  sluitGewoon();
  sluitLijst();
  return delen.join("\n");
}

/** HTML van een mail. Bestaande HTML blijft ongemoeid (gelijk aan `naar_html`). */
export function naarHtml(tekst: string | null | undefined): string {
  if (!tekst || isHtml(tekst)) return tekst ?? "";
  const schoon = tekst.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/^\n+|\n+$/g, "");
  const blokken = schoon.split(/\n[ \t]*\n/).filter((b) => b.trim());
  return blokken.map((blok) => blokNaarHtml(blok.split("\n"))).join("\n");
}

// ── Link uit de werkbalk ─────────────────────────────────────────────

/** Een getypt adres naar een href: zonder schema wordt het https of mailto. */
export function normaliseerAdres(invoer: string): string {
  const adres = invoer.trim();
  if (!adres) return "";
  if (/^(https?:|mailto:)/i.test(adres)) return adres;
  if (/^[^\s/@]+@[^\s/@]+\.[^\s/@]+$/.test(adres)) return `mailto:${adres}`;
  return `https://${adres}`;
}

// ── Open plekken ─────────────────────────────────────────────────────

const HOEK_PLEK = /<\s*(code|kortingscode|kortings_code|retourlabel|retour_label|link|naam)\s*>/gi;

/**
 * Een open plek in hoekhaken (`<naam>`, `<kortingscode>`) wordt `[NAAM]`.
 *
 * In platte tekst herkennen `plaatshouderMelding` en de server zo'n plek.
 * In de editor zou hij als tekst ge-escaped worden (`&lt;naam&gt;`) en
 * dan door beide controles glippen; als HTML zou de editor hem als
 * onbekende tag weggooien. In rechte haken blijft hij herkenbaar. Een
 * `<code>` met een `</code>` erna is een echte tag en blijft staan, net
 * als in `plaatshouderMelding`.
 */
export function plekkenZichtbaar(tekst: string): string {
  return tekst.replace(HOEK_PLEK, (geheel: string, naam: string, positie: number) => {
    if (naam.toLowerCase() === "code") {
      const erna = tekst.slice(positie + geheel.length).toLowerCase();
      const sluit = erna.indexOf("</code>");
      const volgende = erna.indexOf("<code");
      if (sluit !== -1 && (volgende === -1 || sluit < volgende)) return geheel;
    }
    return `[${naam.toUpperCase()}]`;
  });
}

// ── Editor in en uit ─────────────────────────────────────────────────

/** Wat de editor laadt: HTML direct, platte tekst eerst naar alinea's. */
export function naarEditor(waarde: string | null | undefined): string {
  const tekst = plekkenZichtbaar(waarde ?? "");
  return isHtml(tekst) ? tekst : naarHtml(tekst);
}

/**
 * Wat de editor naar buiten geeft, netjes voor een mailprogramma.
 *
 * - Een lijstpunt met één alinea wordt `<li>tekst</li>` (ProseMirror zet
 *   er altijd een `<p>` in; in een mail geeft dat witruimte per punt).
 * - Lege alinea's aan begin en eind vallen weg (de editor zet er een
 *   achter een lijst); een lege alinea ertussen wordt `<p><br></p>`,
 *   anders valt de witregel die JP typte in de mail weg.
 */
export function uitEditor(html: string): string {
  let uit = html.replace(/<li><p>((?:(?!<\/?p[\s>]).)*?)<\/p><\/li>/g, "<li>$1</li>");
  uit = uit.replace(/^(?:<p><\/p>)+|(?:<p><\/p>)+$/g, "");
  uit = uit.replace(/<p><\/p>/g, "<p><br></p>");
  // Een slotalinea met alleen een regelovergang is een lege regel onderaan
  // de mail (recensie blok 3).
  uit = uit.replace(/(?:<p>(?:<br\s*\/?>)+<\/p>)+$/g, "");
  return uit;
}

/**
 * Leesbare tekst uit HTML van de editor, voor de controle op open plekken
 * (sessie 70, recensie blok 3): `&lt;naam&gt;` wordt weer `<naam>` en een
 * deels vetgedrukte `[KORTINGS<strong>CODE</strong>]` wordt `[KORTINGSCODE]`.
 * Platte tekst blijft zoals hij is.
 */
export function tekstVoorControle(waarde: string | null | undefined): string {
  if (!waarde) return "";
  if (!isHtml(waarde)) return waarde;
  const doc = new DOMParser().parseFromString(waarde, "text/html");
  return doc.body.textContent ?? "";
}

/**
 * Tekst uit HTML met regels en alinea's, zelfde regels als
 * `mail_opmaak.naar_tekst` op de server: voor de copiloot, die platte tekst
 * leest en schrijft (recensie blok 3).
 */
export function htmlNaarTekst(waarde: string | null | undefined): string {
  if (!waarde) return "";
  if (!isHtml(waarde)) return waarde;
  let t = waarde.replace(/<(style|script|head)[^>]*>[\s\S]*?<\/\1>/gi, "");
  t = t.replace(/<img[^>]*>/gi, "");
  t = t.replace(/\s+/g, " ");
  t = t.replace(/<br\s*\/?>/gi, "\n");
  t = t.replace(/<li[^>]*>/gi, "\n- ");
  t = t.replace(/<\/(p|div|ul|ol|blockquote|h[1-6]|table)>/gi, "\n\n");
  t = t.replace(/<\/tr>/gi, "\n");
  t = t.replace(/<[^>]+>/g, "");
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${t.replace(/</g, "&lt;")}`, "text/html");
  t = (doc.body.textContent ?? "").replace(/\u00a0/g, " ");
  t = t.split("\n").map((r) => r.trim()).join("\n");
  t = t.replace(/\n{3,}/g, "\n\n").replace(/\n\n(- )/g, "\n$1");
  return t.trim();
}

